import { Injectable, inject } from '@angular/core';
import { UserMasterProxyService } from '../proxies/user-master.proxy.service';
import { jwtDecode } from 'jwt-decode';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { LoginBody } from '../models/user-master/commands/login-body';
import { Router } from '@angular/router';
import { JwtPayload } from '@card-software/nexus-models';
import { environment } from '@environments/environment';
import {
  AuthService,
  MasterDataService,
  RolesBootstrapService,
  SharedTokenService,
  SiteBootstrapService,
} from '@card-software/nexus-utilities';

@Injectable({
  providedIn: 'root',
})
export class UserAuthenticationService {
  private readonly siteBootstrapService = inject(SiteBootstrapService);
  private readonly rolesBootstrapService = inject(RolesBootstrapService);
  private readonly tokenKey = 'SierraToken';

  constructor(
    private userMasterProxyService: UserMasterProxyService,
    private router: Router,
  ) {}

  public login(loginBody: LoginBody): Observable<boolean> {
    return this.userMasterProxyService.login(loginBody).pipe(
      switchMap((response) => {
        if (response.success && response.data) {
          this.saveToken(response.data);
          MasterDataService.initialize(environment.proxyUrls.itemApi, response.data);
          AuthService.initializeFromToken(response.data);
          this.siteBootstrapService.reset();
          this.rolesBootstrapService.clearCache();
          this.siteBootstrapService.initializeSites(response.data);
          return this.rolesBootstrapService.fetchRolesBySite().pipe(map(() => true));
        } else {
          console.error('Login failed', response.errorMessage);
          return of(false);
        }
      }),
      catchError((error) => {
        console.error('Error logging in', error);
        return of(false);
      }),
    );
  }

  public logout() {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem('SierraSites');
    SharedTokenService.clearToken();
    this.siteBootstrapService.reset();
    this.router.navigate(['/login']);
  }

  private saveToken(token: string) {
    localStorage.setItem(this.tokenKey, token);
    SharedTokenService.saveToken(token);
  }
  public get userLite() {
    const token = this.getStoredJwtToken();
    if (!token) {
      return null;
    }

    // Decode the token and map to user details
    const { FirstName, LastName, Email, Username }: JwtPayload = jwtDecode<JwtPayload>(token);
    return {
      firstName: FirstName,
      lastName: LastName,
      email: Email,
      username: Username,
    };
  }

  public get isUserAuthenticated(): boolean {
    const token = this.getStoredJwtToken();
    if (!token) {
      return false;
    }
    const isTokenExpired = this.isTokenExpired(token);
    if (isTokenExpired) {
      this.logout();
      return false;
    }
    return true;
  }

  getStoredJwtToken() {
    return localStorage.getItem(this.tokenKey);
  }

  isTokenExpired(token: string) {
    try {
      const { exp }: { exp: number } = jwtDecode(token);
      return exp * 1000 < Date.now();
    } catch (e) {
      console.error('Error decoding token', e);
      return true;
    }
  }

  public validateTokenAndAuthenticate(token: string): boolean {
    if (!token || this.isTokenExpired(token)) {
      console.warn('Token is invalid or expired');
      return false;
    }
    this.saveToken(token);
    return true;
  }

  /**
   * Global roles (union across every site) — no longer travel in the JWT, this
   * reads the set already populated by RolesBootstrapService.fetchRolesBySite().
   */
  public get roles(): string[] {
    return AuthService.listGlobalRoles();
  }
}
